/* eslint-disable @next/next/no-img-element */
import LoadingArea from 'components/elements/LoadingArea'
import SearchRecommendations from 'components/elements/SearchRecommendations'
import Stack from 'components/elements/Stack'
import { Know } from 'interfaces/Know'
import { appKnowRoute, appSearchRoute, knowRoute } from 'lib/constants/routes'
import _useTranslation from 'lib/hooks/_useTranslation'
import { useState, useCallback, useEffect } from 'react'
import InfiniteScroll from 'react-infinite-scroller'
import { trackPromise } from 'react-promise-tracker'
import { ApiError } from 'services/ApiService'
import SearchService from 'services/SearchService'
import { ButtonText } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import styled, { css } from 'styled-components'
import { useRouter } from 'next/router'
import { getReferrerScreen } from 'lib/formatting/url'
import { SearchSuggestion } from 'interfaces/Search'
import { trackEvent } from 'lib/tracking/analytics-service'
import { AnalyticsEventSearchResultPage } from 'lib/constants/AnalyticsEventType'
import { uuidv4 } from 'lib/tracking/attribution/uuid'
import { formatSearchResultCountText, generateDefaultSearchSuggestion } from 'lib/features/search'
import { parseLocale } from 'lib/constants/localization'
import { SearchOptions } from 'interfaces/TrackingSnippets'
import { getQueryParam } from 'lib/constants/URLs'
import SubjectsKnow from './SubjectsKnow'
import FeedKnowCard from 'stories/elements/Know/FeedKnowCard'
import MonetizationRedirectDialog from 'lib/features/Pro/MonetizationRedirectDialog'
import { useToggleState } from 'lib/hooks/useToggleState'
import ContentMatchingAsset from 'assets/app/knows/content-matching.png'
import { AppState } from 'redux/reducer'
import { UserPremiumStatus } from 'interfaces/User'
import { useSelector } from 'react-redux'

const loadingAreas = {
    search: 'searchResults',
}

const Container = styled(Stack)<{ isApp?: boolean }>`
    padding-top: ${({ isApp }) => (isApp ? '125px' : '70px')};
    @media (max-width: 750px) {
        padding-top: 32px;
    }
`

const KnowsWrapper = styled.div<{ isApp: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 20px;

    ${({ isApp }) =>
        !isApp
            ? css`
                  display: grid;
                  grid-template-columns: 1fr 1fr;
                  gap: 20px;
                  @media (max-width: 1050px) {
                      grid-template-columns: 1fr;
                  }
              `
            : ''}
`
const FixedWrapper = styled(Stack)<{ isDarkTheme?: boolean; isApp?: boolean }>`
    width: 100%;
    max-width: ${({ isApp }) => (!isApp ? '100%' : '650px')};
    margin: 0 auto;
    ${({ isApp }) =>
        isApp
            ? css`
                  position: fixed;
                  z-index: 51;
                  top: 70px;
                  background-color: #2c2742;
                  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.3);
                  border-radius: 0 0 ${theme.borderRadius.webappCard} ${theme.borderRadius.webappCard};
                  padding: 25px;
              `
            : ''}
    ${({ isDarkTheme }) =>
        isDarkTheme &&
        css`
            z-index: 51;
            background-color: #2c2742;
            box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.3);
            border-radius: 0 0 ${theme.borderRadius.webappCard} ${theme.borderRadius.webappCard};
            padding: 25px;
        `}
`

const EmtpyImageWrapper = styled.div`
    display: flex;
    justify-content: center;
`

interface Props {
    searchUrl?: string
    isApp: boolean
    autoFocus?: boolean
    isDarkTheme?: boolean
}

const SearchResults = ({ searchUrl, isApp, autoFocus, isDarkTheme }: Props) => {
    const [filterOptions, setFilterOptions] = useState<{ searchQuery: string; subjectId?: number }>({
        searchQuery: '',
    })
    const [showMonetizationRedirectDialog, toggleMonetizationRedirectDialog] = useToggleState(false)
    const [knows, setKnows] = useState<Know[]>()
    const router = useRouter()
    const [searchResultCount, setSearchResultCount] = useState<number | null>(null)
    const [searchSessionUuid, setSeachSessionUuid] = useState(uuidv4())
    const [selected100PercentMatchKnows, setSelected100PrecentMatchKnows] = useState<string[]>([])
    const [hasMorePages, setHasMorePage] = useState(false)
    const { t: tCommon } = _useTranslation('common')
    const { t } = _useTranslation('app')
    const { t: tPlusPlan } = _useTranslation('plusPlan')
    const referrerScreen = getReferrerScreen(router)
    const parsedLocale = parseLocale(router.locale)
    const authenticatedUser = useSelector((state: AppState) => state.user)

    const SEARCH_QUERY_LENGTH_FOR_TOP_KNOWS_THRESHOLD = 2
    const KNOWS_COUNT_FOR_TOP_KNOWS_THRESHOLD = 8

    const isUserNotPremium = authenticatedUser && authenticatedUser?.premiumStatus === UserPremiumStatus.None

    const regenerateURL = (searchSuggestion: SearchSuggestion) => {
        const { text, subject } = searchSuggestion

        router.push({
            pathname: searchUrl ?? appSearchRoute,
            query: { query: text, subjectId: subject?.id, utm_content: referrerScreen },
        })
    }

    const generateSearchOption = (searchSuggestion: SearchSuggestion, numberOfExactResults: number): SearchOptions => ({
        suggestion: searchSuggestion,
        origin: 'search',
        numberOfExactResults,
        searchSessionUuid,
        type: 'KNOW',
    })

    const loadKnows = useCallback(
        (pageNumber: number, searchSuggestion: SearchSuggestion) => {
            const { text, subject } = searchSuggestion
            const promise = SearchService.getKnows(text, 20, pageNumber, subject?.id, parsedLocale.contentLanguageCode)
                .then((response) => {
                    const shouldTopKnowsBeSelected =
                        isApp &&
                        response.page === 0 &&
                        response.numberOfExactResults > 0 &&
                        text.length > SEARCH_QUERY_LENGTH_FOR_TOP_KNOWS_THRESHOLD &&
                        response.knows.length > KNOWS_COUNT_FOR_TOP_KNOWS_THRESHOLD &&
                        isUserNotPremium

                    if (shouldTopKnowsBeSelected) {
                        setSelected100PrecentMatchKnows(response.knows.slice(0, 3).map((result) => result.know?.uuid ?? ''))
                    }

                    const resultKnows = response.knows.map((result) => result.know) as Know[]
                    if (pageNumber) {
                        setKnows((knows) => (knows?.length ? [...knows, ...resultKnows] : resultKnows))
                    } else {
                        setKnows(resultKnows)
                    }

                    setSearchResultCount(response.totalElements)
                    setHasMorePage(response.hasMore)

                    if (pageNumber === 0) {
                        setFilterOptions({
                            searchQuery: searchSuggestion.text,
                            subjectId: subject?.id,
                        })
                        trackEvent(AnalyticsEventSearchResultPage, {
                            value: searchSuggestion.text,
                            referrerScreen,
                            search: {
                                suggestion: searchSuggestion,
                                origin: 'search',
                                numberOfExactResults: response.numberOfExactResults,
                                searchSessionUuid,
                                type: 'KNOW',
                            },
                        })
                    }
                })
                .catch((error) => {
                    if (error instanceof ApiError) {
                        error.handleUnknownError(tCommon, 'search.list')
                    } else {
                        throw error
                    }
                })

            if (pageNumber === 0) {
                trackPromise(promise, loadingAreas.search)
            }
        },
        [tCommon]
    )

    useEffect(() => {
        const query = getQueryParam('query')
        const subjectId = getQueryParam('subjectId')

        loadKnows(0, generateDefaultSearchSuggestion((query as string) ?? '', { id: subjectId ? +subjectId : undefined }))
    }, [loadKnows, router.query])

    const handleSearch = (searchSuggestion: SearchSuggestion) => {
        setSeachSessionUuid(uuidv4())
        regenerateURL(searchSuggestion)
    }

    const generateSearchSnippet = (knowsCount: number) =>
        generateSearchOption(
            generateDefaultSearchSuggestion(filterOptions.searchQuery, {
                id: filterOptions.subjectId,
            }),
            knowsCount
        )

    return (
        <Container gutter={15} isApp={isApp}>
            <FixedWrapper gutter={5} isDarkTheme={isDarkTheme} isApp={isApp}>
                <SearchRecommendations
                    isSmall
                    defaultValue={filterOptions.searchQuery}
                    placeholder={tCommon('common/searchByTitleLabel')}
                    onSuggestionClicked={handleSearch}
                    origin="search"
                    searchSessionUuid={searchSessionUuid}
                    autoFocus={autoFocus}
                    isDarkTheme={isDarkTheme}
                />
                <ButtonText
                    fontFamily="var(--font-inter)"
                    fontWeight={600}
                    color={isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.placeholderText}
                >
                    {formatSearchResultCountText(searchResultCount, t)}
                </ButtonText>
            </FixedWrapper>
            <MonetizationRedirectDialog
                isOpen={showMonetizationRedirectDialog}
                onClose={toggleMonetizationRedirectDialog}
                title={tPlusPlan('plusPlan/monetizationDialogMatchingContentTitle')}
                image={ContentMatchingAsset}
                type="matching-content"
            />
            <InfiniteScroll
                pageStart={0}
                loadMore={(page: number) =>
                    loadKnows(
                        page,
                        generateDefaultSearchSuggestion(filterOptions.searchQuery, { id: filterOptions.subjectId })
                    )
                }
                hasMore={hasMorePages}
                threshold={800}
            >
                <LoadingArea area={loadingAreas.search}>
                    <KnowsWrapper isApp={isApp}>
                        {knows
                            ? knows.map((know) => (
                                  <FeedKnowCard
                                      know={know}
                                      isDarkTheme={isApp || isDarkTheme}
                                      key={know.uuid}
                                      knowUuid={know.uuid}
                                      referrerScreen="search"
                                      disabledLinking={selected100PercentMatchKnows.includes(know.uuid)}
                                      onClick={() => {
                                          if (selected100PercentMatchKnows.includes(know.uuid)) {
                                              toggleMonetizationRedirectDialog()
                                          }
                                      }}
                                      is100PercentMatch={selected100PercentMatchKnows.includes(know.uuid)}
                                      path={
                                          isApp
                                              ? appKnowRoute(know.uuid, {
                                                    referrerScreen: 'search',
                                                    query: filterOptions?.searchQuery,
                                                })
                                              : knowRoute(know.uuid, {
                                                    referrerScreen: 'search',
                                                    query: filterOptions?.searchQuery,
                                                })
                                      }
                                      search={generateSearchSnippet(knows.length)}
                                  />
                              ))
                            : null}
                    </KnowsWrapper>
                </LoadingArea>
            </InfiniteScroll>
            {searchResultCount !== null && searchResultCount === 0 ? (
                <EmtpyImageWrapper>
                    <SubjectsKnow />
                </EmtpyImageWrapper>
            ) : null}
        </Container>
    )
}

export default SearchResults
